import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { PieChart, Plus, X, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["dining", "groceries", "shopping", "transport", "utilities", "housing", "entertainment", "health", "subscriptions", "other"];

export default function Budgets() {
  const [budgets, setBudgets] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ category: "dining", monthly_limit: "" });

  const load = async () => setBudgets((await api.get("/budgets")).data);
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    await api.post("/budgets", { category: form.category, monthly_limit: parseFloat(form.monthly_limit) });
    toast.success("Budget saved");
    setShow(false); setForm({ category: "dining", monthly_limit: "" });
    load();
  };
  const del = async (id) => { await api.delete(`/budgets/${id}`); load(); toast.success("Deleted"); };

  const color = (s) => s === "over" ? "#E06A3B" : s === "warning" ? "#E9C46A" : "#2A9D8F";

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <PieChart className="w-6 h-6 text-[color:var(--gold)]" />
            <h1 className="font-heading text-3xl font-bold">Category Budgets</h1>
          </div>
          <p className="text-[color:var(--text-secondary)]">Set a monthly cap per category. Get gentle alerts when you're drifting.</p>
        </div>
        <button data-testid="add-budget-btn" onClick={() => setShow(true)} className="gold-btn px-5 py-2.5 rounded-full inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> New budget
        </button>
      </div>

      {budgets.length === 0 ? (
        <div className="glass-card p-16 text-center">
          <PieChart className="w-12 h-12 mx-auto text-[color:var(--gold)] mb-4" />
          <h2 className="font-heading text-xl font-semibold mb-2">No budgets yet</h2>
          <p className="text-[color:var(--text-secondary)] text-sm mb-6">Set your first category cap and start seeing where your money actually goes.</p>
          <button onClick={() => setShow(true)} className="gold-btn px-6 py-3 rounded-full">Add first budget</button>
        </div>
      ) : (
        <div className="space-y-3">
          {budgets.map((b) => (
            <motion.div key={b.budget_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              data-testid={`budget-${b.category}`} className="glass-card p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-medium capitalize">{b.category}</div>
                  <div className="text-xs text-[color:var(--text-muted)]">${b.spent.toLocaleString()} of ${b.monthly_limit.toLocaleString()} this month</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className={`font-mono font-bold text-lg`} style={{ color: color(b.status) }}>{b.pct}%</div>
                  <button data-testid={`del-budget-${b.budget_id}`} onClick={() => del(b.budget_id)} className="w-8 h-8 rounded-lg hover:bg-[rgba(224,106,59,0.1)] flex items-center justify-center text-[color:var(--text-muted)] hover:text-[color:var(--terracotta)] transition"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="h-2 rounded-full bg-[rgba(255,255,255,0.05)] overflow-hidden">
                <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min(100, b.pct)}%` }} transition={{ duration: 0.8 }}
                  className="h-full rounded-full" style={{ background: color(b.status) }} />
              </div>
              {b.status === "over" && <div className="text-xs text-[color:var(--terracotta)] mt-2">Over by ${Math.abs(b.remaining).toLocaleString()} — dial back or lift the cap</div>}
              {b.status === "warning" && <div className="text-xs text-[color:var(--amber,#E9C46A)] mt-2">${b.remaining.toLocaleString()} left · pace carefully</div>}
              {b.status === "healthy" && <div className="text-xs text-[color:var(--emerald)] mt-2">${b.remaining.toLocaleString()} left</div>}
            </motion.div>
          ))}
        </div>
      )}

      {show && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShow(false)}>
          <motion.form initial={{ scale: 0.9 }} animate={{ scale: 1 }} onSubmit={save}
            className="glass-card p-8 max-w-md w-full space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl font-bold">New budget</h2>
              <button type="button" onClick={() => setShow(false)}><X className="w-5 h-5 text-[color:var(--text-muted)]" /></button>
            </div>
            <select data-testid="budget-category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] outline-none capitalize">
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input data-testid="budget-limit" required type="number" step="0.01" placeholder="Monthly limit ($)"
              value={form.monthly_limit} onChange={e => setForm({ ...form, monthly_limit: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] focus:border-[color:var(--gold)] outline-none" />
            <button data-testid="budget-submit" type="submit" className="gold-btn w-full py-3 rounded-xl">Save budget</button>
          </motion.form>
        </div>
      )}
    </div>
  );
}
